import { create } from 'zustand';
import { apiDelete, apiGet, apiPatch, apiPost, getApiOrigin, getFetchAuthHeaders } from '../lib/api.js';

export type ChatMessage = {
  id: string;
  role: 'GATOR' | 'USER';
  content: string;
  createdAt: string;
  /** Present on GATOR messages only — drives voice vs. text answer UI. */
  inputType?: 'voice' | 'text' | 'both' | 'code';
  /** Question complexity — present on GATOR messages only. */
  complexity?: 'LOW' | 'MEDIUM' | 'HIGH';
  /** Suggested answer time in seconds — present on GATOR messages only. */
  estimatedSeconds?: number | null;
};

export type ReportJobStatus = 'none' | 'queued' | 'processing' | 'ready' | 'failed';

export type StageRow = {
  id: string;
  stageNumber: number;
  status: 'LOCKED' | 'UNLOCKED' | 'COMPLETED';
  durationSeconds?: number | null;
  completedAt?: string | null;
  skippedAt?: string | null;
  questionsCount?: number | null;
  userAnswersCount?: number | null;
  reportStatus?: ReportJobStatus;
};

export type LanguageAssessmentRow = {
  id: string;
  simulationId: string;
  language: string;
  level: string;
  /** Plain string, JSON string, or parsed {"en":"...","pl":"..."} object */
  notes: string | Record<string, unknown> | null;
  completedAt: string | null;
  cefrEstimate?: string | null;
  errors?: Array<{
    type: string;
    quote: string;
    fix: string;
    severity?: string;
  }> | null;
  roleReadiness?: {
    verdict: 'sufficient' | 'risky' | 'insufficient';
    risk: 'low' | 'medium' | 'high';
    /** Plain string or JSON: {"en":"...","pl":"..."} */
    note: string | null;
  } | null;
  scenarios?: Array<{
    /** Plain string or JSON: {"en":"...","pl":"..."} */
    scenario: string;
    verdict: 'ok' | 'risky' | 'blocker';
    /** Plain string or JSON: {"en":"...","pl":"..."} */
    note: string | null;
  }> | null;
  /** Plain string, JSON string, or parsed {"en":"...","pl":"..."} object */
  summary?: string | Record<string, unknown> | null;
  /** False when the answers were not gradeable — `level` is then a default A1 floor. */
  assessable?: boolean;
  unassessableReason?: string | null;
};

export type SimulationDetail = {
  id: string;
  userId: string;
  /** English-canonical role. Use `useLocalizedRole()` to display it. */
  role: string;
  /** Polish label captured at creation; null for roles with no translation. */
  rolePl?: string | null;
  experienceLevel: string;
  companyType: string;
  yearsOfExperience: string;
  skills: string[];
  interviewType: string;
  primaryLanguage: string;
  assessmentLanguage?: string | null;
  status: string;
  createdAt: string;
  completedAt?: string | null;
  stages: StageRow[];
  languageAssessment?: LanguageAssessmentRow | null;
  languageAssessmentStatus?: ReportJobStatus;
  finalReportStatus?: ReportJobStatus;
  /** Final report score when the report is ready; null otherwise. */
  overallScore?: number | null;
  languageCheckSkippedAt?: string | null;
  deletedAt?: string | null;
};

export type ActiveQuestionTimer = {
  secondsLeft: number | null;
  overtime: boolean;
  estimatedSeconds: number;
};

export type SetupConfig = {
  /** English-canonical role; `rolePl` carries its Polish label for display. */
  role?: string;
  rolePl?: string | null;
  experienceLevel?: string;
  companyType?: string;
  skills?: string[];
  interviewType?: string;
  primaryLanguage?: string;
  assessmentLanguage?: string | null;
};

const CURRENT_SIMULATION_STORAGE_KEY = 'ig-current-simulation-id';
type LoaderOptions = { suppressGlobalLoader?: boolean; showGlobalLoader?: boolean };

function readPersistedCurrentSimulationId() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(CURRENT_SIMULATION_STORAGE_KEY);
}

function persistCurrentSimulationId(id: string | null) {
  if (typeof window === 'undefined') return;
  if (id) localStorage.setItem(CURRENT_SIMULATION_STORAGE_KEY, id);
  else localStorage.removeItem(CURRENT_SIMULATION_STORAGE_KEY);
}

function refreshAuthPack() {
  void import('./authStore.js').then(({ useAuthStore }) => useAuthStore.getState().refreshProfile());
}

type SimulationState = {
  activeSimulations: SimulationDetail[];
  currentSimulationId: string | null;
  currentSimulation: SimulationDetail | null;
  currentStage: StageRow | null;
  messages: ChatMessage[];
  isStreaming: boolean;
  isThinking: boolean;
  activeQuestionTimer: ActiveQuestionTimer | null;
  languageCheckTotal: number;
  languageCheckCurrent: number;
  setupConfig: SetupConfig;
  setSetupConfig: (partial: SetupConfig) => void;
  createSimulation: () => Promise<{ simulation: SimulationDetail; stages: StageRow[] }>;
  loadActiveSimulations: (preferredSimulationId?: string | null, options?: LoaderOptions) => Promise<void>;
  switchToSimulation: (id: string) => Promise<void>;
  deleteSimulation: (id: string) => Promise<void>;
  loadSimulation: (id: string, stageNumber?: number, options?: LoaderOptions) => Promise<void>;
  setCurrentStageByNumber: (stageNumber: number) => void;
  refreshMessages: (stageId: string) => Promise<void>;
  completeStage: (stageId: string, durationSeconds?: number, opts?: { skipped?: boolean }) => Promise<void>;
  addMessage: (message: ChatMessage) => void;
  setIsStreaming: (v: boolean) => void;
  setIsThinking: (v: boolean) => void;
  setActiveQuestionTimer: (timer: ActiveQuestionTimer | null) => void;
  setLanguageCheckProgress: (current: number, total: number) => void;
  skipLanguageCheck: (id: string) => Promise<void>;
  skipLanguageCheckBestEffort: (id: string) => Promise<void>;
  reset: () => void;
};

function buildCreateBody(config: SetupConfig): Record<string, unknown> {
  const c = config;
  const yearsOfExperienceByLevel: Record<string, string> = {
    JUNIOR: '0-2',
    MID: '2-5',
    SENIOR: '5+',
    LEAD: '8+',
  };
  if (
    !c.role ||
    !c.experienceLevel ||
    !c.companyType ||
    !Array.isArray(c.skills) ||
    !c.interviewType ||
    !c.primaryLanguage
  ) {
    throw new Error('Incomplete setup configuration');
  }
  return {
    role: c.role,
    experienceLevel: c.experienceLevel,
    companyType: c.companyType,
    yearsOfExperience: yearsOfExperienceByLevel[c.experienceLevel] ?? '3-5',
    skills: c.skills,
    interviewType: c.interviewType,
    primaryLanguage: c.primaryLanguage,
    assessmentLanguage: c.assessmentLanguage ?? null,
  };
}

export const useSimulationStore = create<SimulationState>((set, get) => ({
  activeSimulations: [],
  currentSimulationId: readPersistedCurrentSimulationId(),
  currentSimulation: null,
  currentStage: null,
  messages: [],
  isStreaming: false,
  isThinking: false,
  activeQuestionTimer: null,
  languageCheckTotal: 4,
  languageCheckCurrent: 1,
  setupConfig: {},

  setSetupConfig: (partial) =>
    set((s) => ({
      setupConfig: { ...s.setupConfig, ...partial },
    })),

  createSimulation: async () => {
    const body = buildCreateBody(get().setupConfig);
    const res = await apiPost<{ simulation: SimulationDetail; stages: StageRow[] }>(
      '/simulations',
      body,
      { showGlobalLoader: true },
    );
    set({ currentSimulationId: res.simulation.id });
    persistCurrentSimulationId(res.simulation.id);
    await get().loadActiveSimulations(res.simulation.id, { showGlobalLoader: true });
    refreshAuthPack();
    return res;
  },

  loadActiveSimulations: async (preferredSimulationId, options) => {
    const requestConfig = options?.suppressGlobalLoader
      ? { suppressGlobalLoader: true }
      : options?.showGlobalLoader
        ? { showGlobalLoader: true }
        : undefined;
    const res = await apiGet<{ simulations: SimulationDetail[] }>('/simulations/active', requestConfig);
    const simulations = res.simulations ?? [];
    const persisted = preferredSimulationId ?? get().currentSimulationId ?? readPersistedCurrentSimulationId();
    const nextCurrentSimulationId =
      (persisted && simulations.some((s) => s.id === persisted) ? persisted : null) ??
      simulations[0]?.id ??
      null;

    set({ activeSimulations: simulations, currentSimulationId: nextCurrentSimulationId });
    persistCurrentSimulationId(nextCurrentSimulationId);

    if (!nextCurrentSimulationId) {
      set({ currentSimulation: null, currentStage: null, messages: [] });
      return;
    }

    if (get().currentSimulation?.id !== nextCurrentSimulationId) {
      await get().loadSimulation(nextCurrentSimulationId, undefined, options);
    }
  },

  switchToSimulation: async (id) => {
    set({ currentSimulationId: id });
    persistCurrentSimulationId(id);
    await get().loadSimulation(id, undefined, { showGlobalLoader: true });
  },

  deleteSimulation: async (id) => {
    const wasCurrent = get().currentSimulationId === id;
    await apiDelete<{ success: true }>(`/simulations/${id}`);
    await get().loadActiveSimulations();
    refreshAuthPack();

    if (wasCurrent) {
      const nextId = get().currentSimulationId;
      if (!nextId) {
        set({ currentSimulation: null, currentStage: null, messages: [] });
      }
    }
  },

  loadSimulation: async (id, stageNumber, options) => {
    const requestConfig = options?.suppressGlobalLoader
      ? { suppressGlobalLoader: true }
      : undefined;
    const sim = await apiGet<SimulationDetail>(`/simulations/${id}`, requestConfig);
    const stage =
      stageNumber != null
        ? sim.stages.find((s) => s.stageNumber === stageNumber) ?? null
        : sim.stages.find((s) => s.status === 'UNLOCKED') ?? sim.stages[0] ?? null;

    // Also patch activeSimulations so the dashboard shows up-to-date stage
    // statuses immediately without needing a full refetch.
    const activeSimulations = get().activeSimulations.map((s) =>
      s.id === id ? sim : s,
    );

    set({ currentSimulationId: id, currentSimulation: sim, currentStage: stage, messages: [], activeSimulations });
    persistCurrentSimulationId(id);
    if (stage) {
      const msgs = await apiGet<ChatMessage[]>(`/stages/${stage.id}/messages`, requestConfig);
      set({ messages: msgs });
    }
  },

  setCurrentStageByNumber: (stageNumber) => {
    const sim = get().currentSimulation;
    if (!sim) return;
    const stage = sim.stages.find((s) => s.stageNumber === stageNumber) ?? null;
    set({ currentStage: stage, messages: [] });
    if (stage) void get().refreshMessages(stage.id);
  },

  refreshMessages: async (stageId) => {
    const msgs = await apiGet<ChatMessage[]>(`/stages/${stageId}/messages`);
    set({ messages: msgs });
  },

  completeStage: async (stageId, durationSeconds, opts) => {
    const suppressLoaderConfig = { suppressGlobalLoader: true };
    const body = { durationSeconds, skipped: opts?.skipped === true };
    // Completion is the one write that must not be lost — a transient failure
    // here leaves the stage "startable" on the dashboard even though the user
    // finished it. Retry once before surfacing the error.
    try {
      await apiPatch(`/stages/${stageId}/complete`, body, suppressLoaderConfig);
    } catch {
      await new Promise((resolve) => setTimeout(resolve, 800));
      await apiPatch(`/stages/${stageId}/complete`, body, suppressLoaderConfig);
    }
    const sim = get().currentSimulation;
    if (sim) await get().loadSimulation(sim.id, undefined, { suppressGlobalLoader: true });
  },

  addMessage: (message) =>
    set((s) => ({
      messages: [...s.messages, message],
    })),

  setIsStreaming: (v) => set({ isStreaming: v }),
  setIsThinking: (v) => set({ isThinking: v }),
  setActiveQuestionTimer: (timer) => set({ activeQuestionTimer: timer }),
  setLanguageCheckProgress: (current, total) =>
    set({ languageCheckCurrent: current, languageCheckTotal: total }),

  skipLanguageCheck: async (id) => {
    await apiPatch(`/simulations/${id}/language-check/skip`, {});
    await get().loadSimulation(id, undefined, { suppressGlobalLoader: true });
  },

  skipLanguageCheckBestEffort: async (id) => {
    try {
      const origin = getApiOrigin();
      const url = origin
        ? `${origin}/api/v1/simulations/${id}/language-check/skip`
        : `/api/v1/simulations/${id}/language-check/skip`;
      await fetch(url, {
        method: 'PATCH',
        keepalive: true,
        headers: await getFetchAuthHeaders({ contentType: 'application/json' }),
        body: JSON.stringify({}),
      });
    } catch {
      // Best-effort only — page is tearing down, nothing more we can do here.
    }
  },

  reset: () => {
    persistCurrentSimulationId(null);
    set({
      activeSimulations: [],
      currentSimulationId: null,
      currentSimulation: null,
      currentStage: null,
      messages: [],
      isStreaming: false,
      isThinking: false,
      activeQuestionTimer: null,
      languageCheckTotal: 4,
      languageCheckCurrent: 1,
      setupConfig: {},
    });
  },
}));
